import React, { useEffect, useState } from 'react';
import { useGetUsers } from '../../hooks/db/useGetUsers';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Pencil, Mail } from 'lucide-react' 
import { toast } from 'react-toastify';
import { useLoading } from '../../contexts/LoadingContext';
import { useLoginUser } from '../../hooks/db/useLogin';
import PaginationControls from '../PaginationControls/PaginationControls';
import { formatDateTime } from '../../lib/common';

const Users: React.FC = () => {
    const { getUsers } = useGetUsers()
    const navigate = useNavigate()
    const [users, setUsers] = useState<Record<string, any>[]>([])
    const { forgotPasswordUser } = useLoginUser()
    const { setLoading } = useLoading()

    const [totalCount, setTotalCount] = useState(0)
    const pageSize = parseInt(process.env.REACT_APP_PAGE_SIZE || '10', 10)

    const [searchParams, setSearchParams] = useSearchParams()
    const initialPage = parseInt(searchParams.get('page') || '1', 10)
    const [currentPage, setCurrentPage] = useState(initialPage)

    const fetchUsers = async (page: number) => {
        setLoading(true)
        try {
            let filters = {}
            const result = await getUsers(page, pageSize, filters)
            if (result && 'users' in result) {
                setUsers((result as { users: any }).users)
                setTotalCount(result.totalCount)
            }
        } catch (error) {
            if ((error as Error).name === 'AbortError') {
                console.log("Fetch aborted");
            } else {
                console.error("Error fetching users:", error);
                if (error instanceof Error) {
                    toast.error(error.message)
                } else {
                    toast.error("An unknown error occurred")
                }
                toast.error("Error fetching users", {
                    position: "bottom-center",
                })
            }
        }finally{
            setLoading(false)
        }
    }


      
    async function handleResetPasswordEmail(row: any) {
        if(!row.email){
            toast.error("Email not found", {
                position: "bottom-center",
            })
        }
        try {
                setLoading(true)
                // Save user details to Firestore
                const result = await forgotPasswordUser({
                    email: row.email,
                })
                
                if (result.success) {
                    toast.success(result.message, {
                        position: "top-center",
                    })
        
                } else {
                    toast.error("Email not found", {
                        position: "bottom-center",
                    })
                    
                    return
                }
        
            } catch (error) {
                if (error instanceof Error) {
                    console.log(error.message)
                    toast.error(error.message, {
                        position: "bottom-center",
                    })
                    
                } else {
                    console.log("An unknown error occurred")
                }
                
            }finally{
                setLoading(false)
            }
        }

        function handleEdit(row: any) {
            navigate(`/admin/users/edit/${row._id}`)
        }


        useEffect(() => {
            fetchUsers(currentPage)
        }, [currentPage])

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Users</h2>
            <button
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                onClick={() => navigate('/admin/users/add-user')}
            >
                Add User
            </button>
            </div>
            <table className="min-w-full table-auto rounded-lg border text-sm text-left">
                <thead className="bg-gray-200">
                    <tr>
                    <th className="px-4 py-2 border-b text-gray-700">Name</th>
                    <th className="px-4 py-2 border-b text-gray-700">Email</th>
                    <th className="px-4 py-2 border-b text-gray-700">Role</th>
                    <th className="px-4 py-2 border-b text-gray-700">Company Name</th>
                    <th className="px-4 py-2 border-b text-gray-700">Phone</th>
                    <th className="px-4 py-2 border-b text-gray-700">Created At</th>
                    <th className="px-4 py-2 border-b text-gray-700">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {users.map((row, index) => (
                    <tr
                        key={index}
                        className="hover:bg-gray-50 cursor-pointer"
                    >
                        <td
                        className="px-4 py-2 border-b "
                       
                        width="0%"
                        >
                        {row.name}
                        </td>
                        <td className="px-4 py-2 border-b">{row.email}</td>
                        <td className="px-4 py-2 border-b">{row.role}</td>
                        <td className="px-4 py-2 border-b">{row.companyName}</td>
                        <td className="px-4 py-2 border-b">{row.phone}</td>
                        <td className="px-4 py-2 border-b">
                            {formatDateTime(row.createdAt)}
                        </td>
                        <td className="px-4 py-2 border-b">
                            <div className="flex space-x-2">
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleResetPasswordEmail(row)}
                                title='Send Reset Password Email'
                                >
                                <Mail className="h-4 w-4 mr-1" />
                                
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEdit(row)}
                                className="text-gray-800"
                                title='Edit'
                                >
                                <Pencil className="h-4 w-4 mr-1" />
                                
                            </Button>
                            </div>
                        </td>
                    </tr>
                    ))}
                </tbody>
            </table>
            <PaginationControls
                currentPage={currentPage}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={(newPage) => {
                    setCurrentPage(newPage)
                    setSearchParams({ page: newPage.toString() })
                }}
            />
        </div>
    )
}

export default Users